// src/monitoring/components/SourceDialog.jsx
//
// Add or edit a monitored park. Two halves, deliberately separate: what the
// park IS (plant parameters - what the expected-production model and the
// alarm rules need) and where its data COMES FROM (the vendor portal and its
// credentials, rendered from the vendor's own spec).

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Grid, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { useFile } from "../../context/FileContext";
import { useMonitoringText } from "../i18n";

const TIMEZONES = ["Europe/Athens", "Europe/Sofia", "Europe/Bucharest", "Europe/Berlin", "Europe/Paris", "Europe/Amsterdam", "Europe/Budapest", "UTC"];

const emptyForm = (vendor) => ({
  name: "", asset_id: "", vendor: vendor || "", vendor_site_id: "", poll_interval_min: "", enabled: true, credentials: {},
  plant: { dc_kwp: "", ac_kw: "", lat: "", lon: "", timezone: "Europe/Athens", tilt_deg: "25", azimuth_deg: "180", mounting: "fixed",
           commissioned_on: "", module_model: "", inverter_model: "", inverter_count: "", pr_target: "0.80", system_loss_pct: "14" },
});

function fromSource(src) {
  const f = emptyForm(src.vendor);
  return {
    ...f, name: src.name || "", asset_id: src.asset_id || "", vendor: src.vendor, vendor_site_id: src.vendor_site_id || "",
    poll_interval_min: src.poll_interval_min ?? "", enabled: src.enabled !== false,
    plant: { ...f.plant, ...Object.fromEntries(Object.entries(src.plant || {}).filter(([k]) => k in f.plant).map(([k, v]) => [k, v ?? ""])) },
  };
}

export default function SourceDialog({ open, onClose, onSubmit, vendors, source }) {
  const t = useMonitoringText();
  const { currentProject } = useFile();
  const portals = useMemo(() => (vendors || []).filter((v) => !["simulated", "csv_replay"].includes(v.key)), [vendors]);
  const [form, setForm] = useState(() => (source ? fromSource(source) : emptyForm(portals[0]?.key)));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setForm(source ? fromSource(source) : emptyForm(portals[0]?.key)); setError(null); }
  }, [open, source, portals]);

  const spec = useMemo(() => (vendors || []).find((v) => v.key === form.vendor), [vendors, form.vendor]);
  const assets = useMemo(() => {
    const pf = currentProject?.config?.portfolio;
    return (pf?.assets || []).map((a) => ({ id: a.id, name: a.name || a.id, config: a.id === pf.activeAssetId ? currentProject.config : a.config }));
  }, [currentProject]);

  const setPlant = (k, v) => setForm((f) => ({ ...f, plant: { ...f.plant, [k]: v } }));
  const setCred = (k, v) => setForm((f) => ({ ...f, credentials: { ...f.credentials, [k]: v } }));
  const pickAsset = (id) => {
    const a = assets.find((x) => x.id === id);
    setForm((f) => {
      const next = { ...f, asset_id: id };
      if (a?.config) {
        const c = a.config;
        next.plant = { ...f.plant };
        if (!f.name) next.name = a.name;
        if (!f.plant.lat && c.loc_info?.lat != null) next.plant.lat = String(c.loc_info.lat);
        if (!f.plant.lon && c.loc_info?.lon != null) next.plant.lon = String(c.loc_info.lon);
        if (c.timeZone) next.plant.timezone = c.timeZone;
        if (c.panelOrientation?.tilt != null) next.plant.tilt_deg = String(c.panelOrientation.tilt);
        if (c.panelOrientation?.azim != null) next.plant.azimuth_deg = String(c.panelOrientation.azim);
        if (c.mountType === "tracking" || c.mountType === "tracker") next.plant.mounting = "tracker_1axis";
      }
      return next;
    });
  };

  const requiredMissing = !form.name.trim() || !form.vendor || !form.plant.dc_kwp || !form.plant.lat || !form.plant.lon;
  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const plant = Object.fromEntries(Object.entries(form.plant).filter(([, v]) => v !== "" && v !== null && v !== undefined));
      const creds = Object.fromEntries(Object.entries(form.credentials).filter(([, v]) => String(v || "").trim() !== ""));
      await onSubmit({
        name: form.name.trim(), vendor: form.vendor, vendor_site_id: form.vendor_site_id.trim() || null, asset_id: form.asset_id || null,
        credentials: creds, plant, poll_interval_min: form.poll_interval_min === "" ? null : Number(form.poll_interval_min), enabled: form.enabled,
      });
      onClose();
    } catch (e) {
      setError(e.message || t("form.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{source ? t("form.title.edit") : t("form.title.new")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("form.section.identity")}</Typography>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" required label={t("form.name")} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth select size="small" label={t("form.asset")} value={form.asset_id} onChange={(e) => pickAsset(e.target.value)} helperText={t("form.assetHint")}>
                  <MenuItem value="">{t("form.assetNone")}</MenuItem>
                  {assets.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
          </Box>
          <Divider />
          <Box>
            <Typography variant="subtitle2">{t("form.section.plant")}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>{t("form.section.plantHint")}</Typography>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" required type="number" label={t("form.dc")} value={form.plant.dc_kwp} onChange={(e) => setPlant("dc_kwp", e.target.value)} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="number" label={t("form.ac")} value={form.plant.ac_kw} onChange={(e) => setPlant("ac_kw", e.target.value)} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" required type="number" label={t("form.lat")} value={form.plant.lat} onChange={(e) => setPlant("lat", e.target.value)} slotProps={{ htmlInput: { step: "0.0001" } }} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" required type="number" label={t("form.lon")} value={form.plant.lon} onChange={(e) => setPlant("lon", e.target.value)} slotProps={{ htmlInput: { step: "0.0001" } }} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField fullWidth select size="small" label={t("form.tz")} value={form.plant.timezone} onChange={(e) => setPlant("timezone", e.target.value)}>
                  {TIMEZONES.map((z) => <MenuItem key={z} value={z}>{z}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField fullWidth select size="small" label={t("form.mounting")} value={form.plant.mounting} onChange={(e) => setPlant("mounting", e.target.value)}>
                  <MenuItem value="fixed">{t("form.mounting.fixed")}</MenuItem>
                  <MenuItem value="tracker_1axis">{t("form.mounting.tracker")}</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="number" label={t("form.tilt")} value={form.plant.tilt_deg} disabled={form.plant.mounting === "tracker_1axis"} onChange={(e) => setPlant("tilt_deg", e.target.value)} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="number" label={t("form.azimuth")} value={form.plant.azimuth_deg} disabled={form.plant.mounting === "tracker_1axis"} onChange={(e) => setPlant("azimuth_deg", e.target.value)} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="date" label={t("form.commissioned")} value={form.plant.commissioned_on} onChange={(e) => setPlant("commissioned_on", e.target.value)} slotProps={{ inputLabel: { shrink: true } }} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="number" label={t("form.inverterCount")} value={form.plant.inverter_count} onChange={(e) => setPlant("inverter_count", e.target.value)} /></Grid>
              <Grid size={{ xs: 12, md: 3 }}><TextField fullWidth size="small" label={t("form.inverterModel")} value={form.plant.inverter_model} onChange={(e) => setPlant("inverter_model", e.target.value)} /></Grid>
              <Grid size={{ xs: 12, md: 3 }}><TextField fullWidth size="small" label={t("form.module")} value={form.plant.module_model} onChange={(e) => setPlant("module_model", e.target.value)} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="number" label={t("form.prTarget")} value={form.plant.pr_target} onChange={(e) => setPlant("pr_target", e.target.value)} helperText={t("form.prTargetHint")} slotProps={{ htmlInput: { step: "0.01", min: 0, max: 1 } }} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" type="number" label={t("form.loss")} value={form.plant.system_loss_pct} onChange={(e) => setPlant("system_loss_pct", e.target.value)} helperText={t("form.lossHint")} /></Grid>
            </Grid>
          </Box>
          <Divider />
          <Box>
            <Typography variant="subtitle2">{t("form.section.source")}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>{t("form.section.sourceHint")}</Typography>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth select size="small" required label={t("form.vendor")} value={form.vendor}
                  onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value, credentials: {}, poll_interval_min: "" }))}>
                  {(source?.vendor === "simulated" ? vendors : portals).map((v) => <MenuItem key={v.key} value={v.key}>{v.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label={t("form.vendorSite")} value={form.vendor_site_id} onChange={(e) => setForm((f) => ({ ...f, vendor_site_id: e.target.value }))} helperText={t("form.vendorSiteHint")} /></Grid>
              {(spec?.credentialFields || []).map((cf) => (
                <Grid key={cf.key} size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth size="small" required={cf.required && !source?.has_credentials} type={cf.secret ? "password" : "text"} label={cf.label}
                    value={form.credentials[cf.key] || ""} onChange={(e) => setCred(cf.key, e.target.value)} helperText={cf.help || undefined} autoComplete="off" />
                </Grid>
              ))}
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField fullWidth size="small" type="number" label={t("form.interval")} value={form.poll_interval_min} placeholder={String(spec?.defaultPollIntervalMin || "")}
                  onChange={(e) => setForm((f) => ({ ...f, poll_interval_min: e.target.value }))} helperText={spec ? t("form.intervalHint", { min: spec.minPollIntervalMin }) : undefined}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: spec?.minPollIntervalMin || 1 } }} />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }} sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel control={<Checkbox checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />} label={t("form.enabled")} />
              </Grid>
            </Grid>
            {source?.has_credentials && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>{t("form.credentialsKept")}</Typography>}
            {spec && (
              <Alert severity={spec.wired ? "success" : "info"} sx={{ mt: 1.5 }} icon={false}>
                {spec.accessHowto && <Typography variant="body2"><strong>{t("form.accessHowto")}:</strong> {spec.accessHowto}</Typography>}
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>{t("form.capabilities")}:</strong> {Object.entries(spec.capabilities || {}).filter(([, v]) => v).map(([k]) => t(`form.cap.${k}`)).join(", ")}
                </Typography>
                {spec.costNote && <Typography variant="body2" sx={{ mt: 0.5 }}><strong>{t("form.costNote")}:</strong> {spec.costNote}</Typography>}
                {!spec.wired && <Typography variant="body2" sx={{ mt: 0.5 }}>{t("sources.wiredHint")}</Typography>}
              </Alert>
            )}
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("form.cancel")}</Button>
        <Button variant="contained" onClick={submit} disabled={saving || requiredMissing}>{t("form.save")}</Button>
      </DialogActions>
    </Dialog>
  );
}
